<app-page-layout
  [pageTitle]="'Access Policies'"
  [subtitle]="'Manage policies that control access to users, groups, APIs, and API groups.'">

  <!-- Actions Bar -->
  <div page-actions class="flex items-center gap-2">
    <p-toast/>
    <p-button
      label="Create Policy"
      icon="pi pi-plus"
      (onClick)="openCreatePolicyDialog()"
      severity="primary"
      size="small"
      outlined
      class="ml-auto"
    ></p-button>
  </div>

  <p-confirmDialog></p-confirmDialog>

  <!-- State Container -->
  <app-state-container
    [state]="viewState()"
    loadingMessage="Loading access policies..."
    loadingSubMessage="Please wait while we fetch your policies"
    emptyIcon="pi-file"
    emptyTitle="No access policies found"
    [emptyMessage]="emptyStateMessage()"
    [showEmptyAction]="true"
    emptyActionLabel="Create Policy"
    emptyActionIcon="pi-plus"
    [emptyActionOutlined]="true"
    [showClearFilters]="showClearFilters()"
    [errorMessage]="errorMessage()"
    (emptyAction)="openCreatePolicyDialog()"
    (clearFilters)="clearFilters()"
    (retry)="retryLoad()"
  >
    <!-- Policies Table -->
    <div class="bg-white dark:bg-zinc-950 rounded-lg border border-surface-200 dark:border-zinc-800">
      <p-table
        [value]="policies()"
        [loading]="loading()"
        [lazy]="true"
        [paginator]="true"
        [rows]="pageSize"
        [totalRecords]="totalRecords()"
        [rowsPerPageOptions]="[10, 20, 50]"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} policies"
        sortMode="single"
        (onLazyLoad)="onLazyLoad($event)"
        [rowHover]="true"
      >
        <ng-template pTemplate="header">
          <tr class="bg-surface-100 dark:bg-zinc-900 text-surface-700 dark:text-zinc-200">
            <th pSortableColumn="name" pResizableColumn style="min-width:180px;" class="bg-surface-100 dark:bg-zinc-900">
              Policy Name
              <p-sortIcon field="name"></p-sortIcon>
            </th>
            <th pResizableColumn style="min-width:200px;" class="text-left bg-surface-100 dark:bg-zinc-900">
              Applied To
            </th>
            <th pSortableColumn="status" pResizableColumn style="min-width:120px;" class="text-left bg-surface-100 dark:bg-zinc-900">
              Status
              <p-sortIcon field="status"></p-sortIcon>
            </th>
            <th pResizableColumn style="min-width:100px;" class="text-center bg-surface-100 dark:bg-zinc-900">
              Actions
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-policy>
          <tr class="border-t border-surface-200 dark:border-surface-700 hover:bg-surface-50 dark:hover:bg-surface-800 transition cursor-pointer" (click)="viewPolicyDetails(policy)">
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300 font-medium">
              {{ policy.name }}
            </td>
            <td class="px-4 py-2 text-surface-700 dark:text-surface-300">
              {{ policy.targetType === 'Identity Group' ? 'User Group' : 'API Group' }}
            </td>
            <td class="px-4 py-2">
              <span
                class="px-2 py-0.5 rounded text-xs font-medium"
                [ngClass]="{
                  'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300': policy.status === 'Active',
                  'bg-gray-200 text-gray-600 dark:bg-surface-700 dark:text-surface-300': policy.status === 'Inactive',
                  'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300': policy.status === 'Draft'
                }"
              >
                {{ policy.status }}
              </span>
            </td>
            <td class="px-4 py-2">
              <div class="flex items-center justify-center gap-2" (click)="$event.stopPropagation()">
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  size="small"
                  outlined
                  (onClick)="editPolicy(policy)"
                  pTooltip="Edit Policy"
                  tooltipPosition="top"
                ></p-button>
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  outlined
                  (onClick)="deletePolicy(policy)"
                  pTooltip="Delete Policy"
                  tooltipPosition="top"
                ></p-button>
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="4" class="text-center py-6 text-surface-500 dark:text-surface-400">
              No access policies found.
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </app-state-container>

  <!-- Create/Edit/View Dialog -->
  <p-dialog
    [(visible)]="showDialog"
    [modal]="true"
    [closable]="true"
    [style]="{ width: '600px' }"
    [header]="viewMode ? 'Policy Details' : (dialogMode === 'create' ? 'Create Access Policy' : 'Edit Access Policy')"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="policyForm" class="space-y-4" autocomplete="off">
      <!-- Policy Name -->
      <div>
        <label class="block text-sm font-medium mb-1">
          Policy Name @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <input pInputText formControlName="name" placeholder="e.g., Dealer Portal Access"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (!viewMode && policyForm.get('name')?.touched && policyForm.get('name')?.invalid) {
          <small class="text-red-500">Policy name is required</small>
        }
      </div>

      <!-- Target Type -->
      <div>
        <label class="block text-sm font-medium mb-1">
          Target Type @if (!viewMode) { <span class="text-red-500">*</span> }
        </label>
        <p-select
          formControlName="targetType"
          [options]="targetTypeOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          placeholder="Select target type"
          [disabled]="viewMode"
          appendTo="body"
        ></p-select>
      </div>

      <div>
        <label class="block text-sm font-medium mb-1">Scopes</label>

        @if (viewMode) {
          <div class="flex flex-wrap gap-2 p-3 border border-surface-300 dark:border-surface-600 rounded-md bg-surface-50 dark:bg-surface-800 min-h-[42px]">
            @for (scope of policyForm.get('scopes')?.value || []; track scope) {
              <span class="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-500/20 dark:text-primary-300">
                {{ scope }}
              </span>
            }
            @if (!policyForm.get('scopes')?.value?.length) {
              <span class="text-surface-400 dark:text-surface-500 text-sm">
                No scopes selected
              </span>
            }
          </div>
        }

        <div class="relative">
          @if (!viewMode) {
            <p-multiSelect
              formControlName="scopes"
              [options]="scopeOptions()"
              optionLabel="label"
              optionValue="value"
              placeholder="Select scopes"
              [filter]="true"
              display="chip"
              [showClear]="true"
              appendTo="body"
              class="w-full"
            >
              <ng-template pTemplate="item" let-option>
                <div class="flex items-center justify-between w-full p-1 hover:bg-surface-100 dark:hover:bg-surface-700 cursor-pointer">
                  <span>{{ option.label }}</span>
                </div>
              </ng-template>

              <!-- Footer with Add Scope button -->
              <ng-template pTemplate="footer">
                <div class="flex items-center justify-center p-1 border-t border-surface-200 dark:border-surface-700">
                  <p-button
                    label="Add New Scope"
                    icon="pi pi-plus"
                    severity="primary"
                    size="small"
                    text
                    (click)="openCreateScopeDialog($event)"
                    class="w-full justify-center"
                  >
                  </p-button>
                </div>
              </ng-template>
            </p-multiSelect>
          }
        </div>
      </div>

      <!-- Status -->
      <div>
        <label class="block text-sm font-medium mb-1">Status</label>
        <p-select
          formControlName="status"
          [options]="statusSelectOptions"
          optionLabel="label"
          optionValue="value"
          class="w-full"
          appendTo="body"
          placeholder="Select status"
          [disabled]="viewMode"
        ></p-select>
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium mb-1">Description</label>
        <textarea pInputTextarea formControlName="description"
                  placeholder="Describe the policy purpose and scope"
                  class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"
                  rows="3"
                  [disabled]="viewMode">
        </textarea>
      </div>
    </form>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <p-button
          label="{{ viewMode ? 'Close' : 'Cancel' }}"
          severity="secondary"
          (onClick)="closeDialog()"
        ></p-button>
        @if (!viewMode) {
          <p-button
            label="{{ dialogMode === 'create' ? 'Create' : 'Save' }}"
            severity="primary"
            [disabled]="policyForm.invalid"
            [loading]="saving()"
            (onClick)="savePolicy()"
          ></p-button>
        }
      </div>
    </ng-template>
  </p-dialog>

  <!-- Create Scope Dialog -->
  <p-dialog
    [(visible)]="showScopeDialog"
    [modal]="true"
    [closable]="true"
    [style]="{ width: '450px' }"
    header="Create New Scope"
    [draggable]="false"
    [resizable]="false"
  >
    <form [formGroup]="scopeForm" class="space-y-4" autocomplete="off">
      <!-- Scope Name -->
      <div>
        <label class="block text-sm font-medium mb-1">Scope Name <span class="text-red-500">*</span></label>
        <input pInputText formControlName="name" placeholder="e.g., customers.read"
               class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"/>
        @if (scopeForm.get('name')?.touched && scopeForm.get('name')?.invalid) {
          <small class="text-red-500">
            @if (scopeForm.get('name')?.errors?.['required']) {
              <span>Scope name is required</span>
            }
            @if (scopeForm.get('name')?.errors?.['pattern']) {
              <span>Only letters, numbers, dots, underscores, and hyphens allowed</span>
            }
          </small>
        }
        <small class="text-surface-500 dark:text-surface-400 block mt-1">
          Format: resource.action (e.g., customers.read, orders.write)
        </small>
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium mb-1">Description</label>
        <textarea pInputTextarea formControlName="description"
                  placeholder="Describe the scope purpose"
                  class="w-full px-3 py-2 border rounded-md bg-white dark:bg-surface-900 dark:border-surface-600"
                  rows="2">
        </textarea>
      </div>
    </form>

    <ng-template pTemplate="footer">
      <div class="flex justify-end gap-2">
        <p-button
          label="Cancel"
          severity="secondary"
          (onClick)="showScopeDialog = false"
        ></p-button>
        <p-button
          label="Create"
          severity="primary"
          [disabled]="scopeForm.invalid"
          [loading]="isCreatingScope"
          (onClick)="createScope()"
        ></p-button>
      </div>
    </ng-template>
  </p-dialog>
</app-page-layout>